import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BasicDialog from './basicDialog';
import ConversionSetupBox from '../components/conversionSetupBox/conversionSetupBox';
import loanBrokerBlueIcon from '../assets/icons/loanBrokerIcon.svg';
import loanBrokerGrayIcon from '../assets/icons/loanBrokerGrayIcon.svg';
import ecomBlueIcom from '../assets/icons/ecomBlueIcon.svg';
import ecomGrayIcom from '../assets/icons/ecomGrayIcon.svg';
import sassBlueIcon from '../assets/icons/sassBlueIcon.svg';
import sassGrayIcon from '../assets/icons/sassIcon.svg';
import otherBlueIcon from '../assets/icons/otherBlueIcon.svg';
import otherGrayIcon from '../assets/icons/otherIcon.svg';
import {
  useSendConversionOrganization,
} from '../apiHooks/mutations/conversionOrganization/sendConversionOrganization';
import { useOrganizationSettingsStore } from '../store/organizationSettingsStore';
import useGetConversionData from '../apiHooks/queries/getConversionData/useGetConversionData';
import LoadingSpinner from '../components/loadingSpinner/loadingSpinner';

interface GoogleConversionsDialogProps {
  open: boolean;
  onClose: () => void;
}

const GoogleConversionsDialog: FC<GoogleConversionsDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState<string>('');
  const organizationSettings = useOrganizationSettingsStore((state) => state.organizationSettings);

  const { mutate: sendOrganization, isLoading: isSending } = useSendConversionOrganization();
  const { data, isLoading } = useGetConversionData('CONVERSION_DATA_DIALOG');

  useEffect(() => {
    if (organizationSettings.organizationType) {
      setSelectedOption(organizationSettings.organizationType);
    }
  }, [organizationSettings.organizationType]);

  useEffect(() => {
    if (data?.data?.data.organizationType) {
      setSelectedOption(data.data.data.organizationType);
    }
  }, [data]);

  const handleOptionSelected = (title: string) => {
    setSelectedOption(title);
  };

  const handleContinue = () => {
    if (!selectedOption) return;
    sendOrganization(selectedOption);
    onClose();
  };

  const isBusy = isLoading || isSending;

  return (
    <BasicDialog
      open={open}
      closeDialog={onClose}
      allowCloseOnBackdrop={false}
      title="chooseOrganization"
      headerColor="white"
      trackingModal
    >
      <div className="relative flex flex-col gap-6">
        {isBusy && <LoadingSpinner center color="primary" />}

        <div className="flex w-full flex-wrap items-center justify-between gap-4">
          <ConversionSetupBox
            title="loanBroker"
            imgSource={selectedOption === 'loanBroker' ? loanBrokerBlueIcon : loanBrokerGrayIcon}
            onSelected={() => handleOptionSelected('loanBroker')}
            selected={selectedOption === 'loanBroker'}
          />
          <ConversionSetupBox
            title="eCommerce"
            imgSource={selectedOption === 'eCommerce' ? ecomBlueIcom : ecomGrayIcom}
            onSelected={() => handleOptionSelected('eCommerce')}
            selected={selectedOption === 'eCommerce'}
          />
          <ConversionSetupBox
            title="saas"
            imgSource={selectedOption === 'saas' ? sassBlueIcon : sassGrayIcon}
            onSelected={() => handleOptionSelected('saas')}
            selected={selectedOption === 'saas'}
          />
          <ConversionSetupBox
            title="other"
            imgSource={selectedOption === 'other' ? otherBlueIcon : otherGrayIcon}
            onSelected={() => handleOptionSelected('other')}
            selected={selectedOption === 'other'}
            isLast
          />
        </div>

        <div className="mt-4 flex w-full justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-[#CEDADF] px-6 py-2 text-sm text-primary hover:bg-[#F5F7F8]"
          >
            {t('cancel')}
          </button>
          <button
            type="button"
            disabled={!selectedOption || isBusy}
            onClick={handleContinue}
            className={`rounded-lg px-6 py-2 text-sm ${
              !selectedOption || isBusy ? 'bg-[#EAEAEA] text-[#828282]' : 'bg-primary text-white'
            }`}
          >
            {t('continue')}
          </button>
        </div>
      </div>
    </BasicDialog>
  );
};

export default GoogleConversionsDialog;

